/**
 * 「分かりやすさ」のための部品。
 *
 * 契約者パネルの設計原則（05-ui.md §8）:
 *   - 専門用語には必ず 1 行の平易な補足を付ける
 *   - ユーザーがやることは常に「次にやるべき 1 つ」に絞る
 * この 2 つを画面ごとに書き分けさせないため、ここに型として用意する。
 */
import * as React from 'react';
import { Alert, AlertDescription, AlertTitle, Tooltip, TooltipContent, TooltipTrigger } from '@mail-sv/ui';
import { HelpCircle } from 'lucide-react';

/** 専門用語。点線の下線 + ホバーで平易な説明を出す。 */
export function Jargon({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="jargon" tabIndex={0} role="note">
          {term}
        </span>
      </TooltipTrigger>
      <TooltipContent>{children}</TooltipContent>
    </Tooltip>
  );
}

/** 補足説明。手順の前提や注意を、警告ではなく「案内」として出す。 */
export function HelpNote({
  title,
  children,
}: {
  title?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Alert variant="info">
      {title ? <AlertTitle>{title}</AlertTitle> : null}
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

/** 手順の番号付きリスト。DNS 設定など「順番どおりにやってもらう」場面で使う。 */
export function StepList({ steps }: { steps: readonly string[] }) {
  return (
    <ol className="space-y-2 text-sm text-foreground">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
            {i + 1}
          </span>
          <span className="min-w-0 flex-1 leading-relaxed">{s}</span>
        </li>
      ))}
    </ol>
  );
}

/** 開閉できる補足。「この設定の意味」を既定で畳んでおくときに使う。 */
export function Disclosure({
  summary,
  children,
  defaultOpen = false,
}: {
  summary: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details className="group rounded-lg border border-border bg-panel-elevated" open={defaultOpen}>
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-medium text-foreground">
        <HelpCircle className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1">{summary}</span>
        <span className="text-xs text-muted-foreground group-open:hidden">開く</span>
        <span className="hidden text-xs text-muted-foreground group-open:inline">閉じる</span>
      </summary>
      <div className="border-t border-border px-3 py-3 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </details>
  );
}
